import { forwardRef } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Chip, Typography } from "@mui/material";
import colors from "assets/theme/base/colors";
import { styles, animation, customStyles } from "components/MDSelect/styles";
import clsx from "clsx";

// Custom Control component
const CustomControl = ({ children, ...props }) => {
  const { startIcon, innerProps, selectProps } = props;
  return (
    <div
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...innerProps}
      className={clsx("md-select-control", selectProps.customControlClass)}
      style={{
        display: "flex",
        alignItems: "center",
        ...props.getStyles("control", props),
        boxShadow: "unset !important",
      }}
    >
      {startIcon && (
        <div style={customStyles.startIconWrapper}>{startIcon}</div>
      )}
      {children}
    </div>
  );
};

// Custom DropdownIndicator component
const CustomDropdownIndicator = (props) => {
  const { selectProps } = props;
  const isOpen = selectProps.menuIsOpen;
  return (
    <div
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props.innerProps}
      style={{
        display: "flex",
        alignItems: "center",
        paddingRight: 8,
      }}
    >
      <ExpandLessIcon
        fontSize="small"
        style={{
          color: "#a2abb9",
          transform: `${isOpen ? "" : "rotate(180deg)"}`,
          transition: "transform 250ms ease",
        }}
      />
    </div>
  );
};

// Custom SingleValue component for single select chips
const CustomSingleValue = ({ children, ...props }) => {
  const { selectProps, data, clearValue } = props;
  const { chipClasses, isClearable, isDisabled } = selectProps;

  return (
    <Chip
      label={data.label || data.title || data.name || String(data.value)}
      size="small"
      variant="outlined"
      className={clsx("md-select-chip", chipClasses)}
      onDelete={isClearable && !isDisabled ? clearValue : undefined}
      style={{
        margin: "2px",
        maxWidth: "200px",
      }}
      sx={{
        "& .MuiChip-label": {
          fontSize: "0.75rem",
          padding: "4px 8px",
        },
        "& .MuiChip-deleteIcon": {
          fontSize: "16px",
          "&:hover": {
            color: "#d32f2f",
          },
        },
      }}
    />
  );
};

// Custom MultiValue component for multi select chips
const CustomMultiValue = ({ children, ...props }) => {
  const { selectProps, removeProps, data } = props;
  const { chipClasses, isDisabled } = selectProps;

  return (
    <Chip
      label={data.label || data.title || data.name || String(data.value)}
      size="small"
      variant="outlined"
      className={clsx("md-select-chip", chipClasses)}
      onDelete={!isDisabled ? removeProps.onClick : undefined}
      style={{
        margin: "2px",
        maxWidth: "200px",
      }}
      sx={{
        "& .MuiChip-label": {
          fontSize: "0.75rem",
          padding: "4px 8px",
        },
        "& .MuiChip-deleteIcon": {
          fontSize: "16px",
          "&:hover": {
            color: "#d32f2f",
          },
        },
      }}
    />
  );
};

// Custom ValueContainer for both single and multi
const CustomValueContainer = ({ children, ...props }) => {
  const { selectProps } = props;
  const { displayChips } = selectProps;

  if (displayChips) {
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "4px",
          padding: "4px 8px",
          minHeight: "38px",
          flex: 1,
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "2px 8px",
        flex: 1,
      }}
    >
      {children}
    </div>
  );
};

const MDSelect = forwardRef(
  (
    {
      size = "medium",
      labelType = "sticky",
      labelText,
      error = false,
      success = false,
      isMultiLine = false,
      isAsync = false,
      startIcon,
      className = "",
      customControlClass = "",
      hiddenBorder = false,
      displayChips = false,
      chipClasses = "",
      required,
      isMulti = false,
      isClearable = false,
      autoComplete = "off",
      isDisabled = false,
      ...rest
    },
    ref
  ) => {
    const classes = animation();

    // Enhanced custom components for both single and multi chips
    const customComponents = {
      ...(startIcon && {
        Control: (props) => <CustomControl {...props} startIcon={startIcon} />,
      }),
      DropdownIndicator: CustomDropdownIndicator,
      ...(displayChips && {
        ValueContainer: CustomValueContainer,
        ...(isMulti
          ? {
              MultiValue: CustomMultiValue,
            }
          : {
              SingleValue: CustomSingleValue,
            }),
      }),
    };

    const selectClasses = clsx(classes.menu, className);

    // Enhanced props
    const enhancedProps = {
      ...rest,
      isMulti,
      isClearable,
      isDisabled,
      displayChips,
      chipClasses,
      placeholder:
        labelType === "sticky" && labelText
          ? labelText
          : rest.placeholder || "Select...",
    };

    return (
      <div style={{ position: "relative" }}>
        {labelText && (
          <Typography
            className="label"
            variant="caption"
            data-sticky={labelType}
            style={{
              marginBottom: 4,
              display: "block",
              ...(labelType === "sticky" && {
                position: "absolute",
                top: 0,
                left: 0,
                transform: "translate(9px, -7px)",
                overflow: "hidden",
                background: "#fff",
                padding: "0 4px",
                zIndex: 1,
                fontSize: "0.625rem",
              }),
            }}
          >
            {labelText}
            {required && !error && <sup>*</sup>}
            {error && <sup style={{ color: "red", marginLeft: 4 }}>*</sup>}
          </Typography>
        )}
        {isAsync ? (
          <AsyncSelect
            {...enhancedProps}
            ref={ref}
            styles={styles(size, error, success, hiddenBorder, isMultiLine)}
            className={selectClasses}
            classNamePrefix="md-select"
            components={customComponents}
            customControlClass={customControlClass}
            autoComplete={autoComplete}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: colors.light.main,
                primary: colors.light.main,
              },
            })}
          />
        ) : (
          <Select
            {...enhancedProps}
            ref={ref}
            styles={styles(size, error, success, hiddenBorder, isMultiLine)}
            className={selectClasses}
            classNamePrefix="md-select"
            components={customComponents}
            customControlClass={customControlClass}
            autoComplete={autoComplete}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: colors.light.main,
                primary: colors.light.main,
              },
            })}
          />
        )}
      </div>
    );
  }
);

// Updated default props
MDSelect.defaultProps = {
  size: "medium",
  error: false,
  success: false,
  className: "",
  customControlClass: "",
  displayChips: false,
  chipClasses: "",
  isMulti: false,
  isClearable: false,
  required: false,
  labelType: "sticky",
  labelText: "",
  autoComplete: "off",
  isDisabled: false,
};

// Updated prop types
MDSelect.propTypes = {
  size: PropTypes.oneOf(["small", "medium", "large"]),
  error: PropTypes.bool,
  success: PropTypes.bool,
  startIcon: PropTypes.node,
  className: PropTypes.string,
  customControlClass: PropTypes.string,
  displayChips: PropTypes.bool,
  chipClasses: PropTypes.string,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  required: PropTypes.bool,
  labelType: PropTypes.oneOf(["sticky", "non-sticky"]),
  labelText: PropTypes.string,
  autoComplete: PropTypes.oneOf(["on", "off"]),
  isDisabled: PropTypes.bool,
};

export default MDSelect;
